import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";

export const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    telephone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset after showing success
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: "", email: "", telephone: "", message: "" });
      onClose();
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50"
            data-testid="contact-modal-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              data-testid="contact-form-modal"
              className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
                <div>
                  <span className="font-mono text-xs text-royal uppercase tracking-widest block mb-1">
                    Send us a message
                  </span>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900">
                    Request a Quote
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  data-testid="close-modal-btn"
                  className="w-10 h-10 flex items-center justify-center hover:bg-slate-100 transition-colors"
                  aria-label="Close modal"
                >
                  <X size={20} className="text-slate-500" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-slate-600">
                      We'll be in touch with you shortly.
                    </p>
                  </motion.div>
                ) : (
                  <>
                    {/* Name */}
                    <div>
                      <label
                        htmlFor="name"
                        className="block font-mono text-xs text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-form-name"
                        className="w-full px-4 py-3 border border-slate-200 bg-slate-50 text-slate-900 focus:outline-none focus:border-royal focus:bg-white transition-colors"
                        placeholder="John Smith"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block font-mono text-xs text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-form-email"
                        className="w-full px-4 py-3 border border-slate-200 bg-slate-50 text-slate-900 focus:outline-none focus:border-royal focus:bg-white transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>

                    {/* Telephone */}
                    <div>
                      <label
                        htmlFor="telephone"
                        className="block font-mono text-xs text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Telephone Number *
                      </label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        required
                        value={formData.telephone}
                        onChange={handleChange}
                        data-testid="contact-form-telephone"
                        className="w-full px-4 py-3 border border-slate-200 bg-slate-50 text-slate-900 focus:outline-none focus:border-royal focus:bg-white transition-colors"
                        placeholder="01704 123456"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label
                        htmlFor="message"
                        className="block font-mono text-xs text-slate-500 uppercase tracking-wider mb-2"
                      >
                        What do you require? *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        data-testid="contact-form-message"
                        className="w-full px-4 py-3 border border-slate-200 bg-slate-50 text-slate-900 focus:outline-none focus:border-royal focus:bg-white transition-colors resize-none"
                        placeholder="Please briefly describe your project requirements..."
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-form-submit"
                      className="w-full btn-primary py-4 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Send Message
                        </>
                      )}
                    </button>

                    <p className="text-xs text-slate-500 text-center">
                      We'll respond to your enquiry within 24 hours.
                    </p>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
